import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ShieldOff } from "lucide-react";

import { EmptyState, LoadingRows } from "../../../components/states";
import { RequireAuth } from "../../../features/auth/RequireAuth";
import { useSession } from "../../../features/auth/session";
import { EventArchiveCard } from "../../../features/events/EventArchiveControl";
import { EventDeletionDangerZone } from "../../../features/events/EventDeletionControl";
import { EventAdminPage } from "../../../features/admin/EventAdminPage";
import { useMembership } from "../../../features/event-experience/use-membership";
import { useEventSettings } from "../../../features/admin/use-event-settings";

export const Route = createFileRoute("/admin/event/$eventId")({
  component: () => (
    <RequireAuth>
      {(user) => (
        <EventAdminRoute
          uid={user.uid}
          email={user.email}
          signOut={user.signOut}
        />
      )}
    </RequireAuth>
  ),
});

function EventAdminRoute({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const { eventId } = Route.useParams();
  const membership = useMembership(eventId, uid);
  const settings = useEventSettings(eventId);
  const session = useSession();
  const navigate = useNavigate();

  if (membership.status === "loading" || settings.status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page">
        <div className="w-full max-w-2xl px-5">
          <LoadingRows rows={4} />
        </div>
      </main>
    );
  }

  if (
    membership.status === "error" ||
    settings.status === "error" ||
    settings.status === "not-found"
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page p-6">
        <EmptyState icon={AlertCircle} title="Something went wrong">
          {membership.status === "error"
            ? membership.error
            : settings.status === "error"
              ? settings.error
              : settings.status === "not-found"
                ? "Event not found"
                : "Unknown error"}
        </EmptyState>
      </main>
    );
  }

  if (membership.status !== "success" || !membership.data.isEventAdmin) {
    // A super admin may delete any event (the event_deletion_requests rule and
    // the DELETE_EVENT handoff both accept the role), but reads the rest of
    // this page only as the event's admin. So she gets the one control she is
    // entitled to, and the way to the rest. The session role is entitlement
    // only: the rules decide whether the request is accepted.
    const superAdmin = session?.platformRole === "super_admin";
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page p-6">
        <div className="w-full max-w-xl space-y-6">
          <EmptyState
            icon={ShieldOff}
            title="Access denied"
            testId="admin-access-denied"
          >
            {superAdmin
              ? "You are not an administrator of this event. To edit it, make yourself its admin under Governance, Event Admins. As a super admin you can archive or delete it here."
              : "You need event administrator access to manage this event."}
          </EmptyState>
          {superAdmin && (
            <EventArchiveCard
              eventId={eventId}
              eventTitle={settings.data.title}
              uid={uid}
              archived={settings.data.archived === true}
              onChanged={settings.reload}
            />
          )}
          {superAdmin && (
            <EventDeletionDangerZone
              eventId={eventId}
              eventTitle={settings.data.title}
              uid={uid}
              onSucceeded={() => void navigate({ to: "/governance" })}
            />
          )}
        </div>
      </main>
    );
  }

  return (
    <EventAdminPage
      eventId={eventId}
      uid={uid}
      event={settings.data}
      reload={settings.reload}
      // Back to the organiser's own list, which no longer holds it.
      onEventDeleted={() => void navigate({ to: "/admin", replace: true })}
      email={email}
      signOut={signOut}
    />
  );
}
